import React, { useState } from "react";
import { invokeLLM, getDisplayMessage } from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export default function BuscaAvancada({ atestados, onResultados, onClose }) {
  const [criterios, setCriterios] = useState("");
  const [buscando, setBuscando] = useState(false);

  const handleBuscarSemantica = async () => {
    if (!criterios.trim()) {
      toast.error("Digite os critérios de busca");
      return;
    }

    setBuscando(true);
    try {
      // Usar IA para busca semântica
      const prompt = `
Analise os seguintes atestados técnicos e retorne APENAS os IDs dos atestados que correspondem aos critérios de busca.

Critérios de busca: ${criterios}

Atestados disponíveis:
${atestados.map(a => `
ID: ${a.id}
Instituição: ${a.instituicao}
Tipos: ${a.tipos?.join(', ') || 'N/A'}
Competências: ${a.competencias_tecnicas?.join(', ') || 'N/A'}
Resumo: ${a.resumo_executivo || 'N/A'}
`).join('\n---\n')}

Retorne apenas os IDs dos atestados que correspondem aos critérios.
Considere sinônimos e termos relacionados na busca.
`;

      const resultado = await invokeLLM({
        prompt,
        response_json_schema: {
          type: "object",
          properties: {
            ids_correspondentes: {
              type: "array",
              items: { type: "string" }
            },
            justificativa: { type: "string" }
          }
        }
      });

      const idsEncontrados = resultado.ids_correspondentes || [];
      const atestadosEncontrados = atestados.filter(a => idsEncontrados.includes(a.id));

      onResultados(atestadosEncontrados);
      toast.success(`${atestadosEncontrados.length} atestado(s) encontrado(s)`);
    } catch (error) {
      console.error("Erro na busca semântica:", error);
      toast.error(getDisplayMessage(error, "Erro ao realizar busca. Tente novamente."));
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div className="space-y-4 py-4">
      <div>
        <Label htmlFor="criterios">Critérios de Busca</Label>
        <Input
          id="criterios"
          placeholder="Ex: desenvolvimento em Java, infraestrutura de rede, suporte técnico..."
          value={criterios}
          onChange={(e) => setCriterios(e.target.value)}
          className="mt-2"
        />
        <p className="text-xs text-slate-500 mt-1">
          Separe múltiplos critérios por vírgula. A busca considera sinônimos e termos relacionados.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Exemplos de busca:</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• "desenvolvimento web, React, Node.js"</li>
          <li>• "infraestrutura cloud, AWS, DevOps"</li>
          <li>• "sustentação de sistemas legados"</li>
          <li>• "central de serviços com mais de 100 usuários"</li>
        </ul>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onClose} disabled={buscando}>
          Cancelar
        </Button>
        <Button
          onClick={handleBuscarSemantica}
          disabled={buscando}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {buscando ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Buscando...
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </>
          )}
        </Button>
      </div>
    </div>
  );
}